// hero.js — the hero (ported from website2-hero.jsx): headline crescendo and the
// full-product showcase — five REAL screens swapping inside one persistent app
// window, with peeking prev/next screens and floating product artifacts.
// NOTE: the design's mouse-parallax effect on the carousel stage is intentionally skipped.
import React from 'react'
import { Link } from 'react-router-dom'
import { N2Ic } from './icons'
import { N2AppNav, N2Fit, N2Sticker, N2Toast, N2TopBar, N2Window } from './shell'
import { N2ScrCalendar, N2ScrDashboard } from './screens'
import { N2ScrBookings, N2ScrClients, N2ScrMessages } from './screens2'
import { n2Ambient } from './ambient'
import { trackGetStartedClick } from './track'

/* cycling toast — three things Nami just did */
const N2_HERO_TOASTS = [
	{ ic: 'mint', icon: 'bell', title: 'New booking · Clover', sub: 'Thu 11:30 AM · booked itself online' },
	{ ic: 'blush', icon: 'chat', title: 'Luna confirmed “C”', sub: 'reminder sent · no chasing' },
	{ ic: 'lav', icon: 'card', title: 'Biscuit paid · $147.60', sub: 'Square · tip included' }
]
export function N2ToastCycle() {
	const [i, setI] = React.useState(0)
	React.useEffect(() => {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
		const id = window.setInterval(() => setI((c) => (c + 1) % N2_HERO_TOASTS.length), 3400)
		return () => window.clearInterval(id)
	}, [])
	return (
		<div className="n2-cycle">
			<div className="n2-cycle-in" key={i}><N2Toast {...N2_HERO_TOASTS[i]} /></div>
			<div className="n2-cycledots" aria-hidden="true">{N2_HERO_TOASTS.map((_, j) => <i key={j} className={j === i ? 'on' : ''}></i>)}</div>
		</div>
	)
}

const N2_FEATURES = [
	{
		id: 'calendar', icon: 'calendar', label: 'Calendar', url: 'nami.pet/calendar', nav: 'calendar', dwell: 6400,
		screen: (on) => <N2ScrCalendar on={on} />,
		float: () => (
			<React.Fragment>
				<div className="n2-fpos para2" style={{ right: -8, top: 150, '--rot': '2deg' }}><N2ToastCycle /></div>
				<N2Sticker blush icon="heart" style={{ left: -12, bottom: 92, transform: 'rotate(-4deg)' }}>Built with groomers</N2Sticker>
			</React.Fragment>
		)
	},
	{
		id: 'dashboard', icon: 'grid', label: 'Dashboard', url: 'nami.pet/dashboard', nav: 'dashboard', dwell: 6400,
		screen: (on) => <N2ScrDashboard on={on} />,
		float: () => (
			<div className="n2-fpos para2" style={{ left: -6, bottom: 88, '--rot': '-2deg' }}>
				<N2Toast ic="mint" icon="zap" title="Waitlist filled a gap" sub="Nori · 2:00 PM · +$55 recovered" />
			</div>
		)
	},
	{
		id: 'bookings', icon: 'inbox', label: 'Bookings', url: 'nami.pet/bookings', nav: 'bookings', dwell: 7000,
		screen: (on) => <N2ScrBookings on={on} />,
		float: () => (
			<div className="n2-fpos para2" style={{ right: 2, bottom: 70, '--rot': '2.5deg' }}>
				<N2Toast ic="lav" icon="bell" title="Approved in one tap" sub="confirmation & reminder — automatic" />
			</div>
		)
	},
	{
		id: 'clients', icon: 'users', label: 'Clients & pets', url: 'nami.pet/clients', nav: 'clients', dwell: 8200,
		screen: (on) => <N2ScrClients on={on} />,
		float: () => (
			<N2Sticker icon="spark" style={{ right: 150, top: -14, transform: 'rotate(2.5deg)' }}>Every quirk, remembered</N2Sticker>
		)
	},
	{
		id: 'messages', icon: 'chat', label: 'Messages + AI', url: 'nami.pet/messages', nav: 'messages', dwell: 9000,
		screen: (on) => <N2ScrMessages on={on} />,
		float: () => (
			<React.Fragment>
				<N2Sticker icon="spark" style={{ right: 150, top: -14, transform: 'rotate(2deg)' }}>One tap to book, mid-groom</N2Sticker>
				<div className="n2-fpos para2" style={{ left: 4, bottom: 62, '--rot': '-2deg' }}>
					<N2Toast ic="lav" icon="calendar" title="Booked from a text" sub="Cooper · Sat 11:00 AM · zero back-and-forth" />
				</div>
			</React.Fragment>
		)
	}
]

/* displacement maps for the liquid-glass lens: R bends X, G bends Y; rgb(128,128,·) = flat.
   Gradients plateau mid-pill so the center stays true and the rims refract like convex glass. */
const N2_MAPX = 'data:image/svg+xml;utf8,' + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='240' height='64'><rect width='240' height='64' fill='rgb(128,128,0)'/><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='0'><stop offset='0' stop-color='rgb(0,128,0)'/><stop offset='0.2' stop-color='rgb(116,128,0)'/><stop offset='0.5' stop-color='rgb(128,128,0)'/><stop offset='0.8' stop-color='rgb(140,128,0)'/><stop offset='1' stop-color='rgb(255,128,0)'/></linearGradient></defs><rect width='240' height='64' rx='32' fill='url(%23g)'/></svg>")
const N2_MAPY = 'data:image/svg+xml;utf8,' + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='240' height='64'><rect width='240' height='64' fill='rgb(128,128,0)'/><defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='rgb(128,0,0)'/><stop offset='0.24' stop-color='rgb(128,114,0)'/><stop offset='0.5' stop-color='rgb(128,128,0)'/><stop offset='0.76' stop-color='rgb(128,142,0)'/><stop offset='1' stop-color='rgb(128,255,0)'/></linearGradient></defs><rect width='240' height='64' rx='32' fill='url(%23g)'/></svg>")

export function N2LiquidDefs() {
	return (
		<svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
			<filter id="n2-liquid" colorInterpolationFilters="sRGB">
				<feImage href={N2_MAPX} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="mx"></feImage>
				<feGaussianBlur in="mx" stdDeviation="3.5" result="mxb"></feGaussianBlur>
				<feDisplacementMap in="SourceGraphic" in2="mxb" scale="46" xChannelSelector="R" yChannelSelector="G" result="dx"></feDisplacementMap>
				<feImage href={N2_MAPY} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="my"></feImage>
				<feGaussianBlur in="my" stdDeviation="3.5" result="myb"></feGaussianBlur>
				<feDisplacementMap in="dx" in2="myb" scale="30" xChannelSelector="R" yChannelSelector="G"></feDisplacementMap>
			</filter>
			<filter id="n2-liquid-soft" colorInterpolationFilters="sRGB">
				<feImage href={N2_MAPX} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="mx"></feImage>
				<feGaussianBlur in="mx" stdDeviation="5" result="mxb"></feGaussianBlur>
				<feDisplacementMap in="SourceGraphic" in2="mxb" scale="22" xChannelSelector="R" yChannelSelector="G"></feDisplacementMap>
			</filter>
		</svg>
	)
}

/* headline — one crescendo: setup in ink, the two adjectives in mesh-colored ink,
   and the payoff sitting in a liquid-glass capsule. Each word arrives the way it means:
   effortless drifts in, modern wipes in crisp, finally easy lands with a spring. */
export function N2Headline({ emph = 'glass', ink = 'gradient', lines = 4 }) {
	const W = (txt, cls, d) => <span className={'n2-w' + (cls ? ' ' + cls : '')} style={{ '--d': d + 'ms' }}>{txt}</span>
	if (lines === 3) {
		// mobile: three lines — subject / adjectives / payoff. Each line is a nowrap block so the
		// breaks land the same at every phone width.
		return (
			<h1 className={'n2-h1 n2-h1-v3 n2-h1-3l ink-' + ink + ' emph-' + emph}>
				<span className="n2-h1-l">{W('Pet', '', 40)} {W('grooming', '', 100)} {W('software', '', 160)}</span>
				<span className="n2-h1-l">{W("that’s", '', 220)} {W('effortless,', 'n2-w-float n2-ink n2-ink-a', 380)} {W('modern,', 'n2-w-wipe n2-ink n2-ink-b', 720)}</span>
				<span className="n2-h1-l">{W('and', '', 880)}{' '}
					<span className="n2-easy">
						<i className="n2-easy-glass" style={{ '--d': '1080ms' }} aria-hidden="true"></i>
						{W('finally easy', 'n2-w-land', 980)}
					</span>{W('.', 'n2-w-land', 980)}
				</span>
			</h1>
		)
	}
	return (
		<h1 className={'n2-h1 n2-h1-v3 ink-' + ink + ' emph-' + emph}>
			{W('Pet', '', 40)} {W('grooming', '', 100)} {W('software', '', 160)} {W("that’s", '', 220)}{' '}
			<br className="n2-h1-br" />
			{W('effortless,', 'n2-w-float n2-ink n2-ink-a', 380)} {W('modern,', 'n2-w-wipe n2-ink n2-ink-b', 720)}{' '}
			{W('and', '', 880)}{' '}
			<span className="n2-easy">
				<i className="n2-easy-glass" style={{ '--d': '1080ms' }} aria-hidden="true"></i>
				{W('finally easy', 'n2-w-land', 980)}
			</span>{W('.', 'n2-w-land', 980)}
		</h1>
	)
}

/* CTA — a single text link: the promise is the call to action */
export function N2Launch() {
	return (
		<Link className="n2-golive" to="/login" onClick={trackGetStartedClick('hero', 'Go live in minutes')}>
			<span className="n2-golive-ic" aria-hidden="true"><N2Ic name="arrow" size={13} sw={2.6} /></span>
			<span className="n2-golive-tx">Go live in minutes and feel the difference today!</span>
		</Link>
	)
}

export function N2Hero({ emph = 'glass', ink = 'gradient', ambient = 'both' }) {
	const stageRef = React.useRef(null)
	const canvasRef = React.useRef(null)
	const [idx, setIdx] = React.useState(0)
	const [auto, setAuto] = React.useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches)
	const [wide, setWide] = React.useState(() => window.matchMedia('(min-width: 1121px)').matches)
	const tabsRef = React.useRef(null)
	const [lens, setLens] = React.useState(null)

	const measure = React.useCallback(() => {
		const row = tabsRef.current
		if (!row) return
		const btn = row.querySelectorAll('.n2-htab')[idx]
		if (btn) setLens({ x: btn.offsetLeft, y: btn.offsetTop, w: btn.offsetWidth, h: btn.offsetHeight })
	}, [idx])
	React.useLayoutEffect(measure, [measure, wide])
	React.useEffect(() => {
		window.addEventListener('resize', measure)
		if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure)
		return () => window.removeEventListener('resize', measure)
	}, [measure])

	React.useEffect(() => {
		const mq = window.matchMedia('(min-width: 1121px)')
		const fn = () => setWide(mq.matches)
		mq.addEventListener('change', fn)
		return () => mq.removeEventListener('change', fn)
	}, [])

	React.useEffect(() => {
		if (!canvasRef.current) return
		const inst = n2Ambient(canvasRef.current, ambient)
		return () => inst && inst.stop()
	}, [ambient])

	// a click jumps to that pill and restarts the dwell cycle from it (progress fill
	// included); `run` forces a fresh fill even on the current pill
	const [run, setRun] = React.useState(0)
	React.useEffect(() => {
		if (!auto) return
		const t = window.setTimeout(() => setIdx((idx + 1) % N2_FEATURES.length), N2_FEATURES[idx].dwell)
		return () => window.clearTimeout(t)
	}, [idx, auto, run])

	const pick = (i) => { setAuto(!window.matchMedia('(prefers-reduced-motion: reduce)').matches); setRun((r) => r + 1); if (i !== idx) setIdx(i) }
	const n = N2_FEATURES.length

	// carousel geometry: measured in px so cards move with pure transforms
	const [geo, setGeo] = React.useState(null)
	React.useLayoutEffect(() => {
		const el = stageRef.current
		if (!el) return
		const calc = () => {
			const W = el.clientWidth
			const C = Math.min(1120, W - 32)
			const s = (W > 1340 ? 300 : 240) / C
			const half = (C * s) / 2
			const dxl = wide ? (8 + half) - W / 2 : -(W / 2 + half + 60)
			setGeo({ C, s, dxl, dxr: -dxl })
		}
		calc()
		const ro = new ResizeObserver(calc)
		ro.observe(el)
		return () => ro.disconnect()
	}, [wide])

	const roleOf = (i) => {
		const off = ((i - idx) % n + n) % n
		return off === 0 ? 'c' : off === 1 ? 'r' : off === n - 1 ? 'l' : (off <= n / 2 ? 'fr' : 'fl')
	}
	const xfRole = (r) => {
		if (!geo) return null
		const { s, dxl, dxr, C } = geo
		const far = C * s * 0.9
		if (r === 'c') return 'translateX(0) scale(1)'
		if (r === 'l') return 'translateX(' + dxl + 'px) scale(' + s + ')'
		if (r === 'r') return 'translateX(' + dxr + 'px) scale(' + s + ')'
		if (r === 'fl') return 'translateX(' + (dxl - far) + 'px) scale(' + (s * 0.9) + ')'
		return 'translateX(' + (dxr + far) + 'px) scale(' + (s * 0.9) + ')'
	}

	return (
		<header className="n2-hero">
			<N2LiquidDefs />
			<div className="n2-mesh" aria-hidden="true"><i className="m1"></i><i className="m2"></i><i className="m3"></i><i className="m4"></i><i className="m5"></i><i className="m6"></i><i className="m7"></i></div>
			{ambient !== 'calm' ? <canvas key={ambient} className="n2-ambient" ref={canvasRef} aria-hidden="true"></canvas> : null}
			<div className="n2-grain" aria-hidden="true"></div>
			<div className="n2-hero-dots" aria-hidden="true"></div>
			<div className="n2-hero-head">
				<N2Headline emph={emph} ink={ink} />
				<p className="n2-hero-sub n2-hero-sub-v3">A new, modern way to run your grooming business, with intelligent features that make your day run itself. <strong className="n2-sub-em">Free, with every feature unlocked.</strong></p>
				<N2Launch />
			</div>

			<div className="n2-htabsrow">
				<div className="n2-aurora" aria-hidden="true"></div>
				<div className="n2-htabs" role="tablist" aria-label="Explore Nami" ref={tabsRef}>
					{lens ? <span className="n2-htab-lens" style={{ transform: 'translate(' + (lens.x - 5) + 'px,' + (lens.y - 3) + 'px)', width: lens.w + 10, height: lens.h + 6 }} aria-hidden="true"></span> : null}
					{N2_FEATURES.map((ft, i) => (
						<button key={ft.id} type="button" role="tab" aria-selected={i === idx}
							className={'n2-htab' + (i === idx ? ' on' : '')} onClick={() => pick(i)}>
							{auto && i === idx ? <i className="n2-htab-fill" key={'r' + idx + '-' + run} style={{ '--dur': ft.dwell + 'ms' }} aria-hidden="true"></i> : null}
							<N2Ic name={ft.icon} size={15} sw={2} />{ft.label}
						</button>
					))}
				</div>
			</div>

			<div className="n2-stage" ref={stageRef}>
				<div className="n2-stage-glow" aria-hidden="true"></div>
				<div className="n2-track para1" style={geo ? { '--invs': +(1 / geo.s).toFixed(3) } : undefined}>
					<div className="n2-track-sizer" aria-hidden="true"><i style={{ height: 46 }}></i><i style={{ aspectRatio: '1240 / 780' }}></i></div>
					{N2_FEATURES.map((ft, i) => {
						const r = roleOf(i)
						const on = r === 'c'
						const peek = wide && (r === 'l' || r === 'r')
						return (
							<div key={ft.id} className={'n2-card r' + r}
								style={geo ? { width: geo.C, marginLeft: -geo.C / 2, transform: xfRole(r) } : { width: '100%', marginLeft: '-50%' }}
								aria-hidden={!on && !peek} role={peek ? 'button' : undefined} tabIndex={peek ? 0 : undefined}
								aria-label={peek ? 'Show ' + ft.label : undefined}
								onClick={peek ? () => pick(i) : undefined}
								onKeyDown={peek ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(i) } } : undefined}>
								<div className="n2-cardin">
									<N2Window url={ft.url}>
										<N2Fit w={1240} h={780}>
											<div className="s-app">
												<N2AppNav active={ft.nav} />
												<div className="s-main">
													<N2TopBar bare={ft.id === 'clients'} />
													<div className="s-body">
														<div className={'n2-scr' + (on ? ' on' : '')}>{ft.screen(on)}</div>
													</div>
												</div>
											</div>
										</N2Fit>
									</N2Window>
									<span className="n2-ctag">{ft.label}</span>
								</div>
							</div>
						)
					})}
				</div>

				{N2_FEATURES.map((ft, i) => (
					<div key={ft.id} className={'n2-hfloat' + (i === idx ? ' on' : '')} aria-hidden={i !== idx}>{ft.float(i === idx)}</div>
				))}
			</div>
		</header>
	)
}
